import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { Link2, User, BookOpen, Layers } from 'lucide-react';

export default function Mappings() {
    const { subjects, sections, faculty, mappings, addMapping, deleteMapping } = useData();
    const [selectedFaculty, setSelectedFaculty] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedSection, setSelectedSection] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (selectedFaculty && selectedSubject && selectedSection) {
            addMapping({
                facultyId: selectedFaculty,
                subjectId: selectedSubject,
                sectionId: selectedSection
            });
            // Reset logic if needed or keep for multiple maps
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to remove this mapping?')) {
            await deleteMapping(id);
        }
    };

    // ... rest of code



    const getFacultyName = (id) => faculty.find(f => f.id === id)?.name || id;
    const getSubjectName = (id) => subjects.find(s => s.id === id)?.name || id;
    const getSectionName = (id) => sections.find(s => s.id === id)?.name || id;

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-2xl font-bold font-heading text-primary">Academic Mappings</h1>
                <p className="text-muted-foreground">Assign subjects and sections to faculty</p>
            </div>

            <div className="bg-card border border-border/50 p-6 rounded-2xl shadow-sm">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Link2 className="text-primary" size={20} />
                    Create New Mapping
                </h2>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="text-sm font-medium text-muted-foreground block mb-1">Faculty</label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <select
                                value={selectedFaculty}
                                onChange={(e) => setSelectedFaculty(e.target.value)}
                                className="w-full bg-secondary border-transparent focus:border-primary focus:ring-0 rounded-lg pl-9 pr-4 py-2.5 appearance-none"
                                required
                            >
                                <option value="">Select Faculty</option>
                                {faculty.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-muted-foreground block mb-1">Subject</label>
                        <div className="relative">
                            <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <select
                                value={selectedSubject}
                                onChange={(e) => setSelectedSubject(e.target.value)}
                                className="w-full bg-secondary border-transparent focus:border-primary focus:ring-0 rounded-lg pl-9 pr-4 py-2.5 appearance-none"
                                required
                            >
                                <option value="">Select Subject</option>
                                {subjects.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-muted-foreground block mb-1">Section</label>
                        <div className="relative">
                            <Layers className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <select
                                value={selectedSection}
                                onChange={(e) => setSelectedSection(e.target.value)}
                                className="w-full bg-secondary border-transparent focus:border-primary focus:ring-0 rounded-lg pl-9 pr-4 py-2.5 appearance-none"
                                required
                            >
                                <option value="">Select Section</option>
                                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <button
                        type="submit"
                        className="bg-primary text-primary-foreground px-6 py-2.5 rounded-lg font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                    >
                        Assign
                    </button>
                </form>
            </div>

            <div className="space-y-4">
                <h3 className="text-lg font-bold font-heading">Active Mappings</h3>
                <div className="bg-card border border-border overflow-hidden rounded-xl">
                    <table className="w-full text-left">
                        <thead className="bg-secondary text-muted-foreground text-xs uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-4 font-semibold">Faculty</th>
                                <th className="px-6 py-4 font-semibold">Subject</th>
                                <th className="px-6 py-4 font-semibold">Section</th>
                                <th className="px-6 py-4 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {mappings.length === 0 ? (
                                <tr><td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">No mappings found</td></tr>
                            ) : (
                                mappings.map(mapping => (
                                    <tr key={mapping.id} className="hover:bg-muted/30 transition-colors">
                                        <td className="px-6 py-4 font-medium">{getFacultyName(mapping.facultyId)}</td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {getSubjectName(mapping.subjectId)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">{getSectionName(mapping.sectionId)}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleDelete(mapping.id)}
                                                className="text-destructive hover:text-destructive/80 text-sm font-medium"
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
